import { useState, useEffect } from 'react';

/**
 * Hook to track a CSS media query match state.
 * @param query - A valid CSS media query string (e.g., '(min-width: 768px)')
 * @returns boolean indicating if the query matches
 */
export function useMediaQuery(query: string): boolean {
    const [matches, setMatches] = useState(() =>
        typeof window !== 'undefined'
            ? window.matchMedia(query).matches
            : false,
    );

    useEffect(() => {
        const mediaQuery = window.matchMedia(query);
        const handler = (e: MediaQueryListEvent) => setMatches(e.matches);

        // Set initial value in case SSR value differs
        setMatches(mediaQuery.matches);

        mediaQuery.addEventListener('change', handler);
        return () => mediaQuery.removeEventListener('change', handler);
    }, [query]);

    return matches;
}

/**
 * Convenience hook to check if the viewport is mobile-sized (< 768px).
 * @returns true if viewport width is less than 768px
 */
export function useIsMobile(): boolean {
    return !useMediaQuery('(min-width: 768px)');
}
